if (!customElements.get('heimat-card-flavors')) {
  // Shared across rails and AJAX-rendered sections. Seed sibling IDs after a
  // response so subsequent cards in the same family can reuse it.
  const requests = new Map();
  const queue = [];
  let active = 0;
  const pump = () => {
    if (active >= 3 || !queue.length) return;
    active++;
    const { task, resolve, reject } = queue.shift();
    task().then(resolve, reject).finally(() => { active--; pump(); });
    pump();
  };
  const load = (productId, market, locale) => new Promise((resolve, reject) => {
    queue.push({ resolve, reject, task: async () => {
      const key = `${market}:${locale}:${productId}`;
      if (requests.has(key)) return requests.get(key);
      const url = new URL('/apps/heimatgut-sku-flavor/products/group', location.origin);
      url.searchParams.set('id', productId);
      url.searchParams.set('market', market);
      url.searchParams.set('locale', locale);
      const pending = fetch(url, { signal: AbortSignal.timeout(10000) }).then(response => {
        if (!response.ok) throw new Error('Flavor lookup failed');
        return response.json();
      }).then(data => {
        const products = Array.isArray(data.products) ? data.products : [];
        for (const product of products) {
          requests.set(`${market}:${locale}:${numericId(product.id)}`, Promise.resolve(products));
        }
        return products;
      }).catch(error => {
        requests.delete(key);
        throw error;
      });
      requests.set(key, pending);
      return pending;
    } });
    pump();
  });
  const numericId = id => String(id).split('/').pop();
  customElements.define('heimat-card-flavors', class extends HTMLElement {
    connectedCallback() {
      this.resizeObserver = new ResizeObserver(() => this.render());
      this.resizeObserver.observe(this);
      document.fonts.ready.then(() => this.render());
      if (this.products) { this.render(); return; }
      if (!this.dataset.productId) return;
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.ensureProducts().catch(() => { /* Leave chips empty when group membership is unknown. */ });
      }, { rootMargin: '200px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }

    render() {
      if (!this.isConnected) return;
      if (!this.products || this.products.length < 2) {
        this.replaceChildren();
        return;
      }
      if (!this.clientWidth) return;
      // Group membership and order come from the app, even for repeated labels.
      const labels = this.products.map(product => product.variant_label || product.title);
      const chip = label => {
        const span = document.createElement('span');
        span.className = 'hg-card__chip';
        span.textContent = label;
        span.title = label;
        return span;
      };
      const flavors = labels.slice(0, 2).map(chip);
      const update = () => {
        const remaining = labels.length - flavors.length;
        this.replaceChildren(...flavors, ...(remaining ? [chip(this.dataset.moreLabel.replace('__COUNT__', remaining))] : []));
      };
      update();
      // Measure full labels and reserve room for the remaining flavor count.
      // If no full label fits beside the count, show the total count on its own.
      const gap = parseFloat(getComputedStyle(this).columnGap) || 0;
      const fits = () => {
        for (const child of this.children) {
          child.style.flexShrink = '0';
          child.style.maxWidth = 'none';
        }
        const width = [...this.children].reduce((total, child) => total + child.getBoundingClientRect().width, 0)
          + (this.children.length - 1) * gap;
        return width <= this.clientWidth;
      };
      while (labels.length > 1 && flavors.length && !fits()) {
        flavors.pop();
        update();
      }
      for (const child of this.children) {
        child.style.removeProperty('flex-shrink');
        child.style.removeProperty('max-width');
      }
    }

    ensureProducts() {
      if (this.productRequest) return this.productRequest;
      this.productRequest = load(this.dataset.productId, this.dataset.market, this.dataset.locale || '').then(products => {
        const current = products.find(product => numericId(product.id) === this.dataset.productId);
        this.products = current ? products : [];
        this.render();
        return this.products;
      });
      return this.productRequest;
    }
  });
}
